import { Currency, Lang, Locale } from '@revolugo/common/constants'
import {
  Event,
  type RevolugoConfig,
  RevolugoElementsPlugin,
} from '@revolugo/elements-vue3'

import { getPosthogClient } from './01.posthog.client.ts'

import type { Plugin } from 'vue'

const toElementLocale = (locale: string): Locale => {
  switch (locale) {
    case Lang.EN.toString():
    case Locale.en_US.toString(): {
      return Locale.en_US
    }
    case Lang.FR.toString():
    case Locale.fr_FR.toString(): {
      return Locale.fr_FR
    }
    case Lang.DE.toString():
    case Locale.de_DE.toString(): {
      return Locale.de_DE
    }
    case Lang.ES.toString():
    case Locale.es_ES.toString(): {
      return Locale.es_ES
    }
    case Lang.IT.toString():
    case Locale.it_IT.toString(): {
      return Locale.it_IT
    }
    case Lang.NL.toString():
    case Locale.nl_NL.toString(): {
      return Locale.nl_NL
    }
    case Lang.PT.toString():
    case Locale.pt_PT.toString(): {
      return Locale.pt_PT
    }
    default: {
      return Locale.en_US
    }
  }
}

const toNuxti18nLocale = (locale: Locale): Lang => {
  switch (locale) {
    case Locale.en_US: {
      return Lang.EN
    }
    case Locale.fr_FR: {
      return Lang.FR
    }
    case Locale.de_DE: {
      return Lang.DE
    }
    case Locale.es_ES: {
      return Lang.ES
    }
    case Locale.it_IT: {
      return Lang.IT
    }
    case Locale.nl_NL: {
      return Lang.NL
    }
    case Locale.pt_PT: {
      return Lang.PT
    }
    default: {
      return Lang.EN
    }
  }
}

export default defineNuxtPlugin(nuxtApp => {
  const config = useRuntimeConfig()
  const { eventBus } = useEventBus()
  const route = useRoute()
  const router = useRouter()

  const optionalHeaders: Record<string, string> = {}
  if (config.public.REVOLUGO_ELEMENTS_CONSUMER_CUSTOM_ID) {
    optionalHeaders['x-consumer-custom-id'] =
      config.public.REVOLUGO_ELEMENTS_CONSUMER_CUSTOM_ID
  }
  if (config.public.REVOLUGO_ELEMENTS_GROUPS) {
    optionalHeaders['x-consumer-groups'] =
      config.public.REVOLUGO_ELEMENTS_GROUPS
  }

  const posthogClient = getPosthogClient()

  function initElements() {
    const locales =
      posthogClient?.getFeatureFlagPayload('feature--locales') || undefined

    const elementsConfig: Partial<RevolugoConfig> = {
      theme: DEFAULT_THEME,
      ...window?.RevolugoElements?.config,
    }

    nuxtApp.vueApp.use(RevolugoElementsPlugin as Plugin, {
      apiEnvironment: config.public.REVOLUGO_BOOKING_API_ENVIRONMENT,
      apiKey: config.public.REVOLUGO_PUBLISHABLE_API_KEY,
      currency: route.query.currency || Currency.EUR,
      installComponents: false,
      lang: toElementLocale(
        (nuxtApp.$i18n as { locale: Ref<string> }).locale.value,
      ),
      locales,
      optionalHeaders,
      theme: DEFAULT_THEME,
      ...elementsConfig,
    })
  }

  if (posthogClient) {
    let elementsInitialized = false

    const timeout = setTimeout(() => {
      if (!elementsInitialized) {
        elementsInitialized = true
        initElements()
      }
    }, 1000)
    posthogClient.onFeatureFlags(() => {
      clearTimeout(timeout)
      if (!elementsInitialized) {
        elementsInitialized = true
        initElements()
      }
    })
  } else {
    initElements()
  }

  eventBus.value.$on(Event.CurrencyUpdated, newCurrency => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({
      query: {
        ...route.query,
        currency: newCurrency,
      },
    })
  })

  eventBus.value.$on(Event.LangUpdated, newLang => {
    ;(nuxtApp.$i18n as { setLocale: (locale: string) => void }).setLocale(
      toNuxti18nLocale(newLang),
    )
  })
})
